import { debounce, defaultsDeep } from 'lodash'

import { breakpoints, generateServices, permissionPresets, projectName, states } from '@/utils'

import render from './render'

import visitedFavorites from '@/utils/mixins/visitedFavorites'

export default function(options = {}) {
  options = Object.assign(
    {
      filterByIsActive: true,
      dense: false
    },
    options
  )

  const localStorageKeys = {
    isActive: `${projectName}:generator:search:${options.name}:isActive`
  }

  const find = defaultsDeep(
    {
      defaultFilter: {
        $search: undefined,
        isActive: undefined
      },
      appendMode: true,
      abortPrevious: true
    },
    options.find
  )

  return {
    name: 'GeneratorSearch',

    mixins: [
      visitedFavorites({ service: options.name }),
      generateServices({
        name: options.name,

        inputFilter: options.inputFilter,
        outputFilter: options.outputFilter,

        find,

        cacher: options.cacher,

        create: false,
        update: false,
        remove: false
      })
    ],

    props: {
      maxWidth: {
        type: Number,
        default: 800
      },

      titleCreate: {
        type: String,
        default: undefined
      }
    },

    data() {
      return {
        isActive: true,

        showFilter: false
      }
    },

    computed: {
      $_show() {
        if (this.viewport.size.width <= breakpoints.md) {
          if (!this.$route.params.id) {
            return true
          } else if (this.$route.params.id) {
            return false
          }
        }

        return true
      },

      $_ready() {
        return this.restData[options.name].find.state === states.ready
      },
      $_loading() {
        return this.restData[options.name].find.state === states.loading
      },

      loadedCount() {
        if (Array.isArray(this.restData[options.name].find.data)) {
          return this.restData[options.name].find.data.length
        }

        return 0
      },

      showLoadMore() {
        return this.loadedCount < this.restData[options.name].find.pagination.total
      },

      hasFilter() {
        return !!this.$slots.filter || typeof this.$scopedSlots.filter === 'function'
      },
      hasReport() {
        return !!this.$slots.report || typeof this.$scopedSlots.report === 'function'
      },
      hasCreate() {
        return !options.excludeCreate || (this.$slots.create || typeof this.$scopedSlots.create === 'function')
      }
    },

    watch: {
      [`restData.${options.name}.find.filter.$search`]() {
        this.rest[options.name].abortFind()
        this.restData[options.name].find.pagination.offset = 0
      },

      'account.id'() {
        this.find()
      }
    },

    created() {
      this.onScroll = debounce(this.onScroll, 500, {
        leading: false,
        trailing: true
      })
    },

    async mounted() {
      await this.getIsActive()
      await this.find()
      this.getVisited()
      this.getFavorites()
    },

    methods: {
      /**
       * Догружает следующие записи при приближении к концу списка.
       *
       * @param {Event} event событие прокрутки контейнера списка
       * @returns {void}
       */
      onScroll(event) {
        const scrollBottom = event.target.scrollHeight - event.target.clientHeight - event.target.scrollTop
        if (scrollBottom <= 160) {
          this.loadMore()
        }
      },

      /**
       * Догружает записи, следующие за уже загруженными.
       * Если смещение совпало с количеством загруженных записей,
       * догрузка невозможна и список загружается с начала.
       *
       * @returns {void}
       */
      loadMore() {
        if (!this.$_ready || !this.showLoadMore) {
          return
        }

        const pagination = this.restData[options.name].find.pagination
        const loadedCount = this.loadedCount

        if (pagination.offset === loadedCount) {
          this.restData[options.name].find.data = []
          if (pagination.offset === 0) {
            this.rest[options.name].find()
          } else {
            pagination.offset = 0
          }

          return
        }

        pagination.offset = loadedCount
      },

      /**
       * Запрашивает записи сервиса, если есть права на просмотр.
       *
       * @returns {Promise<void>}
       */
      async find() {
        const accept = [ 'advanced', options.serviceForCheckPermissions || options.name, options.methodForCheckPermissions || 'find' ].join('.')
        if (this.checkPermissions(accept, permissionPresets.meUp)) {
          await this.rest[options.name].find()
        }
      },

      /**
       * Читает из localStorage признак показа только активных записей.
       * При отсутствии или некорректном значении признак включается.
       *
       * @returns {Promise<void>}
       */
      async getIsActive() {
        let isActive = true
        try {
          isActive = await JSON.parse(window.localStorage.getItem(localStorageKeys.isActive))
        } catch (error) {
          isActive = true
        } finally {
          if (isActive === null || isActive === undefined) {
            isActive = true
          }
          this.setIsActive(isActive)
        }
      },

      /**
       * Сохраняет признак показа только активных записей и применяет его
       * к фильтру поиска.
       *
       * @param {boolean} event показывать только активные записи
       * @returns {void}
       */
      setIsActive(event) {
        if (typeof event !== 'boolean') {
          event = true
        }
        window.localStorage.setItem(localStorageKeys.isActive, event)
        this.isActive = event
        this.toggleFilterByIsActive(event)
      },

      /**
       * Переключает фильтр поиска по признаку активности.
       *
       * @param {boolean} event показывать только активные записи
       * @returns {void}
       */
      toggleFilterByIsActive(event) {
        if (event === false) {
          this.restData[options.name].find.filter.isActive = undefined
        } else {
          this.restData[options.name].find.filter.isActive = true
        }
      }
    },

    render(h) {
      return render.call(this, h, options)
    }
  }
}
